export interface PricingTierCardThemeColors {
  bg: string;
  patternPrimary: string;
  patternSecondary: string;
}

export interface PricingTierCardAppearance {
  light: PricingTierCardThemeColors;
  dark: PricingTierCardThemeColors;
}

export interface PricingTierDraft {
  tempId: string;
  durationDays: number | null;
  prices: Record<string, number>;
  baseCurrency: string;
  promoPrices: Record<string, number> | null;
  promoUntil: string | null;
  isActive: boolean;
  order: number;
  isFamily: boolean;
  familySlots: number;
  imageId: number | null;
  imageUrl: string | null;
  gradientFrom: string | null;
  gradientTo: string | null;
  gradientAngle: number;
  /**
   * Настройка цветов карточки абонемента (light/dark).
   * `null` — использовать дефолтные цвета на платформе.
   * Применяется только для тарифов подписочного плана.
   */
  cardAppearance: PricingTierCardAppearance | null;
  translations: PricingTierTranslationDraft[];
  bonusTemplates: BonusTemplateDraft[];
  /** Языки аудитории, для которых тариф виден. Пустой массив = виден всем. */
  targetLanguages: string[];
}

export const DEFAULT_TIER_CARD_APPEARANCE: PricingTierCardAppearance = {
  light: {
    bg: '#f3f4f6',
    patternPrimary: '#D9D9D9',
    patternSecondary: '#6E6E6E',
  },
  dark: {
    bg: '#1a1a1a',
    patternPrimary: '#3a3a3a',
    patternSecondary: '#8a8a8a',
  },
};

export interface PricingTierTranslationDraft {
  languageCode: string;
  name: string;
  shortDescription?: string | null;
}

export interface BonusTemplateDraft {
  tempId: string;
  type: 'NUMBER' | 'BOOLEAN' | 'ARRAY' | 'STRING';
  initialValue: number | boolean | string[] | string;
  durationMode: 'RELATIVE' | 'ABSOLUTE' | 'INHERIT';
  durationDays: number | null;
  expiresAt: string | null;
  order: number;
  translations: BonusTemplateTranslationDraft[];
}

export interface BonusTemplateTranslationDraft {
  languageCode: string;
  name: string;
  description?: string | null;
}

export function createEmptyTierDraft(order: number): PricingTierDraft {
  return {
    tempId: crypto.randomUUID(),
    durationDays: 30,
    prices: {},
    baseCurrency: 'UZS',
    promoPrices: null,
    promoUntil: null,
    isActive: true,
    order,
    isFamily: false,
    familySlots: 1,
    imageId: null,
    imageUrl: null,
    gradientFrom: null,
    gradientTo: null,
    gradientAngle: 135,
    cardAppearance: null,
    translations: [{ languageCode: 'ru', name: '', shortDescription: null }],
    bonusTemplates: [],
    targetLanguages: [],
  };
}

export function getTierName(tier: PricingTierDraft, lang = 'ru'): string {
  const t = tier.translations.find((tr) => tr.languageCode === lang);
  return t?.name || tier.translations[0]?.name || `Тариф ${tier.order + 1}`;
}

export function getTierPriceDisplay(tier: PricingTierDraft): string {
  const basePrice = tier.prices[tier.baseCurrency];

  // Проверяем, активна ли промо-акция
  const hasPromo = tier.promoPrices !== null
    && tier.promoUntil !== null
    && new Date(tier.promoUntil) > new Date()
    && Object.keys(tier.promoPrices).length > 0;

  if (hasPromo && tier.promoPrices) {
    const promoPrice = tier.promoPrices[tier.baseCurrency];
    const originalDisplay = basePrice !== undefined
      ? `${basePrice.toLocaleString()} ${tier.baseCurrency}`
      : '';
    const promoDisplay = promoPrice !== undefined
      ? `${promoPrice.toLocaleString()} ${tier.baseCurrency}`
      : '';
    return promoDisplay ? `${promoDisplay} (было ${originalDisplay})` : originalDisplay;
  }

  if (basePrice !== undefined) {
    return `${basePrice.toLocaleString()} ${tier.baseCurrency}`;
  }
  const firstEntry = Object.entries(tier.prices)[0];
  if (firstEntry) {
    return `${firstEntry[1].toLocaleString()} ${firstEntry[0]}`;
  }
  return 'Цена не указана';
}
